import React, { useEffect, useRef, useState } from "react";

/** Поля из callback виджета Telegram (hash/auth_date на backend не отправляем). */
export interface TelegramAuthUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date?: number;
  hash?: string;
}

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramAuthUser) => void;
  }
}

const WIDGET_SCRIPT_SRC = "https://telegram.org/js/telegram-widget.js?22";
const BOT_DOMAIN_INVALID_RE = /bot domain invalid/i;
const WIDGET_PROBE_MS = 4000;

type WidgetProbeResult = "valid" | "invalid" | "pending";

function probeWidgetContainer(container: HTMLElement): WidgetProbeResult {
  if (BOT_DOMAIN_INVALID_RE.test(container.textContent ?? "")) {
    return "invalid";
  }
  if (container.querySelector('iframe[src*="telegram"]')) {
    return "valid";
  }
  return "pending";
}

interface TelegramLoginWidgetProps {
  botUsername: string;
  onAuth: (user: TelegramAuthUser) => void;
  onAvailabilityChange?: (ready: boolean) => void;
}

export default function TelegramLoginWidget({
  botUsername: botUsernameProp,
  onAuth,
  onAvailabilityChange,
}: TelegramLoginWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const botUsername = botUsernameProp.replace(/^@/, "");
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    if (isAvailable === true) onAvailabilityChange?.(true);
    if (isAvailable === false) onAvailabilityChange?.(false);
  }, [isAvailable, onAvailabilityChange]);

  useEffect(() => {
    window.onTelegramAuth = onAuth;
    return () => {
      delete window.onTelegramAuth;
    };
  }, [onAuth]);

  useEffect(() => {
    setIsAvailable(null);
  }, [botUsername]);

  useEffect(() => {
    const container = containerRef.current;
    if (!botUsername || !container) return;

    const script = document.createElement("script");
    script.src = WIDGET_SCRIPT_SRC;
    script.async = true;
    script.setAttribute("data-telegram-login", botUsername);
    script.setAttribute("data-size", "large");
    script.setAttribute("data-onauth", "onTelegramAuth(user)");
    script.setAttribute("data-request-access", "write");

    container.replaceChildren(script);

    const applyProbe = (result: WidgetProbeResult) => {
      if (result === "valid") setIsAvailable(true);
      if (result === "invalid") setIsAvailable(false);
    };

    const observer = new MutationObserver(() => {
      const result = probeWidgetContainer(container);
      if (result !== "pending") {
        applyProbe(result);
        observer.disconnect();
      }
    });
    observer.observe(container, { childList: true, subtree: true, characterData: true });

    const timeoutId = window.setTimeout(() => {
      observer.disconnect();
      const result = probeWidgetContainer(container);
      applyProbe(result === "pending" ? "invalid" : result);
    }, WIDGET_PROBE_MS);

    return () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
      container.replaceChildren();
    };
  }, [botUsername]);

  if (!botUsername || isAvailable === false) return null;

  const probing = isAvailable === null;

  return (
    <div
      ref={containerRef}
      style={{
        display: probing ? "none" : "flex",
        justifyContent: "center",
        marginTop: 8,
      }}
      aria-label="Přihlášení přes Telegram"
      aria-hidden={probing}
    />
  );
}
